import type { Client } from "../lib/api";

export interface CTAButton {
  text: string;
  url: string;
}

export interface TrustBadge {
  text: string;
}

export type FormState = {
  // Basic Info
  businessName: string;
  websiteUrl: string;
  // Location
  businessAddress: string;
  regionLanguage: string; // form-only for now
  // GBP
  gbpPrimaryCategory: string;
  gbpSecondaryCategoriesText: string;
  gbpServicesText: string;
  // About
  aboutBusiness: string;
  uniqueSellingPoints: string;
  credentials: string;
  // Voice
  voiceTones: string[];
  pointOfView: string;
  writingModel: string;
  writingVoiceProfile: string;
  wordsToAlwaysUse: string;
  wordsToNeverUse: string;
  // Audience
  targetAudience: string;
  // Reference
  writingSamples: string;
  onboardingNotes: string;
  specificRequests: string;
  // Brand & CTA
  brandPrimaryColor: string;
  brandAccentColor: string;
  phoneNumber: string;
  ctaText: string;
  socialProfiles: string;
  businessLogoUrl: string;
  // Page Template (stored as JSON in localStorage for now; not yet persisted - see Chunk 3)
  // We track them in-form so the inputs work, persist will come with content gen
  heroTagline: string;
  ctaButtons: CTAButton[];
  trustBadges: TrustBadge[];
  // Review Data
  averageRating: string; // string for input, parsed on save
  totalReviewCount: string;
  reviewSourceUrl: string;
  // Local
  localDetails: string;
  // Image preferences
  defaultImageStyle: string;
  brandStyleGuide: string;
  // Prompt Customization (Advanced) — form-only for now; not yet persisted or
  // threaded into generation. Mirrors the Core 30 reference panel layout.
  imageStyleNotes: string;
  customContentPrompt: string;
  // YouTube / Video — form-only for now (OAuth + voiceover live elsewhere).
  videoVoiceover: string;
  // External Link Domains - stored as Set<string>, init from default
  externalLinkDomains: Set<string>;
  customDomainText: string;
  excludedDomainText: string; // form-only excluded-domains chip text
};

// =========================================================================
// Default external link domains - the same list Core 30 uses
// =========================================================================
export const DEFAULT_EXTERNAL_DOMAINS = [
  // Default selection for every client (see DEFAULT_SELECTED_EXTERNAL_DOMAINS).
  "en.wikipedia.org",
  // Core / general dental authorities
  "ada.org",
  "mouthhealthy.org",
  "nidcr.nih.gov",
  "cdc.gov",
  "nih.gov",
  "medlineplus.gov",
  "mayoclinic.org",
  "clevelandclinic.org",
  "hopkinsmedicine.org",
  "healthline.com",
  "webmd.com",
  "health.harvard.edu",
  // Specialty bodies (match to client/article type)
  "aae.org", // endodontists — root canals
  "perio.org", // periodontology — gum disease
  "aaoinfo.org", // orthodontists — braces/Invisalign
  "aaoms.org", // oral & maxillofacial surgeons — extractions/implant surgery
  "aapd.org", // pediatric dentistry
  "aacd.com", // cosmetic dentistry — veneers/whitening
  "prosthodontics.org", // dentures/crowns/prosthodontics
  "aaid.com", // implant dentistry
  "osseo.org", // Academy of Osseointegration — implants
  "aagd.org", // Academy of General Dentistry
  "agd.org", // AGD consumer (KnowYourTeeth)
  "dentalsleep.org", // sleep apnea / oral appliances
  "fluoridealert.org", // fluoride reference
  // Dental schools (high-authority .edu for deeper topics)
  "dental.harvard.edu",
  "dentistry.umich.edu",
  "dental.washington.edu",
  "dentistry.ucla.edu",
  "dental.upenn.edu",
  "dentistry.usc.edu",
];

// fix233: domains pre-checked by default for a NEW client. Per the standing
// rule, only Wikipedia is selected out of the box; the user can enable more in
// Client Settings → External Links. (The full list above is what's OFFERED;
// this is what's CHECKED initially.)
export const DEFAULT_SELECTED_EXTERNAL_DOMAINS = ["en.wikipedia.org"];

// =========================================================================
// Default Image Styles
// =========================================================================
export const IMAGE_STYLE_OPTIONS = [
  { value: "realistic_photography", label: "Realistic Photography" },
  { value: "lifestyle", label: "Lifestyle" },
  { value: "editorial", label: "Editorial" },
  { value: "illustration", label: "Illustration" },
  { value: "studio", label: "Studio / Product" },
];

export function clientToFormState(c: Client): FormState {
  // Render gbpServices object back to text for editing.
  // New format (chunk 3q): CATEGORY (uppercase) + plain service lines.
  // Old format used "### Category" + "- Service" — the parser still accepts
  // either, but new content writes in the new format for cleaner editing.
  let gbpServicesText = "";
  if (c.gbpServices && typeof c.gbpServices === "object") {
    const lines: string[] = [];
    for (const [category, services] of Object.entries(c.gbpServices)) {
      lines.push(category.toUpperCase());
      for (const s of services) lines.push(s);
      lines.push("");
    }
    gbpServicesText = lines.join("\n").trim();
  }

  return {
    businessName: c.businessName ?? "",
    websiteUrl: c.websiteUrl ?? "",
    businessAddress: c.businessAddress ?? "",
    regionLanguage: "English (US)",
    gbpPrimaryCategory: c.gbpPrimaryCategory ?? "",
    gbpSecondaryCategoriesText: (c.gbpSecondaryCategories ?? []).join(", "),
    gbpServicesText,
    aboutBusiness: c.aboutBusiness ?? "",
    uniqueSellingPoints: c.uniqueSellingPoints ?? "",
    credentials: c.credentials ?? "",
    voiceTones: c.voiceTones ?? [],
    pointOfView: c.pointOfView ?? "",
    writingModel: c.writingModel ?? "claude-opus-4-8",
    // fix261: was hardcoded to "" (form-only), so AI-filled values reverted on
    // save/reload. Now loads from the persisted client record.
    writingVoiceProfile: c.writingVoiceProfile ?? "",
    wordsToAlwaysUse: c.wordsToAlwaysUse ?? "",
    wordsToNeverUse: c.wordsToNeverUse ?? "",
    targetAudience: c.targetAudience ?? "",
    writingSamples: c.writingSamples ?? "",
    onboardingNotes: c.onboardingNotes ?? "",
    specificRequests: c.specificRequests ?? "",
    brandPrimaryColor: c.brandPrimaryColor ?? "#2563eb",
    brandAccentColor: c.brandAccentColor ?? "#f0f7ff",
    phoneNumber: c.phoneNumber ?? "",
    ctaText: c.ctaText ?? "",
    socialProfiles: c.socialProfiles ?? "",
    businessLogoUrl: c.businessLogoUrl ?? "",
    heroTagline: "",
    ctaButtons: [],
    trustBadges: [],
    averageRating: c.averageRating != null ? String(c.averageRating) : "",
    totalReviewCount: c.totalReviewCount != null ? String(c.totalReviewCount) : "",
    reviewSourceUrl: c.reviewSourceUrl ?? "",
    localDetails: c.localDetails ?? "",
    defaultImageStyle: c.defaultImageStyle ?? "realistic_photography",
    brandStyleGuide: c.brandStyleGuide ?? "",
    imageStyleNotes: "",
    customContentPrompt: "",
    videoVoiceover: "adam_male",
    externalLinkDomains:
      c.externalLinkDomains && c.externalLinkDomains.length > 0
        ? new Set(c.externalLinkDomains)
        : new Set(DEFAULT_SELECTED_EXTERNAL_DOMAINS),
    customDomainText: "",
    excludedDomainText: (c.excludedDomains ?? []).join(", "),
  };
}

/**
 * Parse the GBP Services textarea content into a category → services map.
 *
 * Accepts BOTH formats forever (no migration needed):
 *
 *   New format (chunk 3q, default for all new content):
 *     CATEGORY                    ← all caps line = category header
 *     Service One                 ← plain line = service under that category
 *     Service Two
 *     - Service Three             ← leading dash also accepted
 *
 *     COSMETIC DENTIST
 *     Teeth Whitening
 *
 *   Old format (### + bullets, kept readable forever):
 *     ### Category Name           ← line starts with "###" = category
 *     - Service One               ← bullet line = service
 *     - Service Two
 *
 * Detection logic per line:
 *   1. If it starts with "###" → old-format category header.
 *   2. Else if it's all uppercase (and matches the category shape) → new-format
 *      category header.
 *   3. Else if a current category exists → service line (strip leading -, *, •).
 *   4. Blank lines are ignored.
 */
export function parseGbpServicesText(
  text: string,
  options: { keepEmptyCategories?: boolean } = {}
): Record<string, string[]> {
  const result: Record<string, string[]> = {};
  let current: string | null = null;

  for (const rawLine of text.split("\n")) {
    const line = rawLine.trim();
    if (!line) continue;

    // 1. Old-format category: "### Heading"
    if (line.startsWith("###")) {
      current = line.replace(/^#+/, "").trim();
      if (current) result[current] = result[current] ?? [];
      continue;
    }

    // 2. New-format category: all-uppercase line (allowing spaces, ampersand,
    //    parens, hyphens, slashes, digits). Must contain at least one letter.
    //    Must be a "category-shaped" line (3-60 chars, no trailing punctuation
    //    that would suggest a service like "Dental Cleanings:").
    if (isNewFormatCategoryHeader(line)) {
      // Title-case for display consistency with the JSON keys downstream.
      current = toTitleCase(line);
      if (current) result[current] = result[current] ?? [];
      continue;
    }

    // 3. Service line (under a category)
    if (current) {
      const item = line.replace(/^[-•*]\s*/, "").trim();
      if (item) result[current].push(item);
    }
  }

  // Drop categories with zero services (likely typos / stray uppercase lines
  // when PASTING raw GBP text). Skipped when keepEmptyCategories is set — the
  // interactive editor needs to preserve a category the user has deliberately
  // emptied (fix266: removing the last service no longer deletes the section).
  if (!options.keepEmptyCategories) {
    for (const [k, v] of Object.entries(result)) {
      if (v.length === 0) delete result[k];
    }
  }

  return result;
}

function isNewFormatCategoryHeader(line: string): boolean {
  // 3-60 chars, contains at least one A-Z letter, no lowercase letters,
  // no trailing colon (that would suggest a sub-heading or labeled line).
  if (line.length < 3 || line.length > 60) return false;
  if (line.endsWith(":")) return false;
  if (!/[A-Z]/.test(line)) return false;
  if (/[a-z]/.test(line)) return false;
  // Allow letters, digits, spaces, &, /, -, parens, period, apostrophe.
  if (!/^[A-Z0-9 &/\-().']+$/.test(line)) return false;
  return true;
}

function toTitleCase(s: string): string {
  return s
    .toLowerCase()
    .split(/(\s+|[&/])/)
    .map((tok) => {
      if (/^\s+$/.test(tok) || tok === "&" || tok === "/") return tok;
      if (tok.length === 0) return tok;
      // Preserve "DIY" / "AI" style acronyms? For now keep simple title case.
      return tok.charAt(0).toUpperCase() + tok.slice(1);
    })
    .join("");
}

// Convert form state to update payload for the API
export function formStateToUpdatePayload(s: FormState) {
  const secondaries = s.gbpSecondaryCategoriesText
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);

  // fix266: keep empty categories so a deliberately-emptied category section
  // persists across save/reload (matches the editor's behavior).
  const services = parseGbpServicesText(s.gbpServicesText, {
    keepEmptyCategories: true,
  });

  const parsedRating = s.averageRating ? Number.parseFloat(s.averageRating) : null;
  const parsedReviewCount = s.totalReviewCount
    ? Number.parseInt(s.totalReviewCount, 10)
    : null;

  return {
    businessName: s.businessName.trim(),
    websiteUrl: s.websiteUrl.trim(),
    businessAddress: s.businessAddress.trim() || null,
    gbpPrimaryCategory: s.gbpPrimaryCategory.trim() || null,
    gbpSecondaryCategories: secondaries.length > 0 ? secondaries : null,
    gbpServices: Object.keys(services).length > 0 ? services : null,
    aboutBusiness: s.aboutBusiness.trim() || null,
    uniqueSellingPoints: s.uniqueSellingPoints.trim() || null,
    credentials: s.credentials.trim() || null,
    voiceTones: s.voiceTones.length > 0 ? s.voiceTones : null,
    pointOfView: s.pointOfView || null,
    writingModel: s.writingModel || "claude-opus-4-8",
    // fix261: persist the Writing Voice Profile (was never sent before).
    writingVoiceProfile: s.writingVoiceProfile.trim() || null,
    wordsToAlwaysUse: s.wordsToAlwaysUse.trim() || null,
    wordsToNeverUse: s.wordsToNeverUse.trim() || null,
    targetAudience: s.targetAudience.trim() || null,
    writingSamples: s.writingSamples.trim() || null,
    onboardingNotes: s.onboardingNotes.trim() || null,
    specificRequests: s.specificRequests.trim() || null,
    brandPrimaryColor: s.brandPrimaryColor || null,
    brandAccentColor: s.brandAccentColor || null,
    phoneNumber: s.phoneNumber.trim() || null,
    ctaText: s.ctaText.trim() || null,
    socialProfiles: s.socialProfiles.trim() || null,
    businessLogoUrl: s.businessLogoUrl.trim() || null,
    averageRating:
      parsedRating != null && Number.isFinite(parsedRating) ? parsedRating : null,
    totalReviewCount:
      parsedReviewCount != null && Number.isFinite(parsedReviewCount)
        ? parsedReviewCount
        : null,
    reviewSourceUrl: s.reviewSourceUrl.trim() || null,
    localDetails: s.localDetails.trim() || null,
    externalLinkDomains:
      s.externalLinkDomains.size > 0
        ? Array.from(s.externalLinkDomains).sort()
        : null,
    excludedDomains: (() => {
      const arr = s.excludedDomainText
        .split(",")
        .map((x) => x.trim().toLowerCase())
        .filter(Boolean);
      return arr.length > 0 ? arr : null;
    })(),
    defaultImageStyle: s.defaultImageStyle || null,
    brandStyleGuide: s.brandStyleGuide.trim() || null,
  };
}
